import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { get } from 'lodash';
import { Observable, map } from 'rxjs';
import {
  DnaNutritionGuideline,
  DnaNutritionGuidelineCalculation,
  DnaNutritionGuidelineFoodRestrictionQueryResult,
  DnaNutritionGuidelineFoodSourceQueryResult,
  DnaNutritionGuidelineMealPlanQueryResult,
  PurchaseProduct,
  Query
} from '@interfaces/schema';
import { DailyLogService } from '@providers/api/daily-log/daily-log';
import { ApiReportService } from '@providers/api/report/report.service';
import {
  DailyFoodLoggingParams,
  DailyFoodMealTypesParams,
  GenerationNourishPlusExecuteResponse,
  GetDailyFoodLoggingResponse,
  GetDailyFoodMealTypesResponse,
  GetEligibilityGenerationNourishPlusResponse,
  GetProcessGenerationNourishPlusResponse
} from './nutrition-guide.interface';

/**
 * Report Nourish Guideline Service.
 *
 * @exports
 * @class ReportNourishGuidelineService
 */
// eslint-disable-next-line no-restricted-syntax
@Injectable({ providedIn: 'root' })
export class ReportNourishGuidelineService extends DailyLogService {
  private readonly apollo = inject(Apollo);
  private readonly reportService = inject(ApiReportService);

  /**
   * Nutrition Guideline Featured Product.
   *
   * @memberof ReportNourishGuidelineService
   * @returns {Observable<Array<PurchaseProduct>>}
   */
  featuredProduct(): Observable<Array<PurchaseProduct>> {
    const query = gql`
      query {
        DNA {
          NutritionGuideline {
            featuredProducts {
              id
              title
              image
              imageApp
              imageWeb
              images {
                id
                pointer
                position
              }
              description
              shortDescription
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'DNA.NutritionGuideline.featuredProducts'
            ) as Array<PurchaseProduct>
        )
      );
  }

  /**
   * Nutrition Guideline Food Restriction.
   *
   * @memberof ReportNourishGuidelineService
   * @returns {Observable<DnaNutritionGuidelineFoodRestrictionQueryResult>}
   */
  foodRestriction(): Observable<DnaNutritionGuidelineFoodRestrictionQueryResult> {
    const query = gql`
      query NutritionGuidelineFoodRestriction(
        $include: [DnaNutritionGuidelineFoodRestrictionInclude]
      ) {
        DNA {
          NutritionGuideline {
            FoodRestriction {
              findByUser(include: $include) {
                count
                results {
                  evidence
                  foodCategory
                  nGuidelineFoodRestrictionType {
                    foodType
                    description
                    exampleFoods
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables: { include: ['RPX_NUTRITION_GUIDELINE_RESTRICTION_TYPE'] }
      })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'DNA.NutritionGuideline.FoodRestriction.findByUser'
            ) as DnaNutritionGuidelineFoodRestrictionQueryResult
        )
      );
  }

  /**
   * Nutrition Guideline Food Source.
   *
   * @memberof ReportNourishGuidelineService
   * @returns {Observable<DnaNutritionGuidelineFoodSourceQueryResult>}
   */
  foodSource(): Observable<DnaNutritionGuidelineFoodSourceQueryResult> {
    const query = gql`
      query NutritionGuidelineFoodSource(
        $include: [DnaNutritionGuidelineFoodSourceInclude]
      ) {
        DNA {
          NutritionGuideline {
            FoodSource {
              findByUser(include: $include) {
                count
                results {
                  nutritionGuidelineFoodGroupId
                  exampleFoods
                  exampleIndonesianFoods
                  valueServingSize
                  nGuidelineFoodGroup {
                    description
                    summary
                    icon
                    isActive
                    position
                    exampleServingSize
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables: { include: ['RPX_NUTRITION_GUIDELINE_FOOD_GROUP'] }
      })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'DNA.NutritionGuideline.FoodSource.findByUser'
            ) as DnaNutritionGuidelineFoodSourceQueryResult
        )
      );
  }

  /**
   * Execute generation nourish plus.
   *
   * @returns {Observable<unknown>}
   * @memberof ReportNourishGuidelineService
   */
  generationNourishPlusExecute(): Observable<GenerationNourishPlusExecuteResponse> {
    return this.reportService.post<GenerationNourishPlusExecuteResponse>(
      'usp/process/generation-nourish-plus/execute',
      {}
    );
  }

  /**
   * Get eligibility for generation nourish plus.
   *
   * @returns {Observable<unknown>}
   * @memberof ReportNourishGuidelineService
   */
  getEligibilityGenerationNourishPlus(): Observable<GetEligibilityGenerationNourishPlusResponse> {
    return this.reportService.get(
      'usp/process/generation-nourish-plus/eligibility'
    );
  }

  /**
   * Meal Types.
   *
   * @param {DailyFoodMealTypesParams} params Parameters.
   * @memberof ReportNourishGuidelineService
   * @returns {Array<string>}
   */
  getMealTypes(
    params: DailyFoodMealTypesParams
  ): Observable<GetDailyFoodMealTypesResponse> {
    return this.get<GetDailyFoodMealTypesResponse>(
      'daily-food-meal-types',
      params
    );
  }

  /**
   * Retrieves overview of user's food logging for a specific date.
   *
   * @memberof ReportNourishGuidelineService
   * @returns {Observable<GetDailyFoodMealTypesResponse>}
   * @param {DailyFoodLoggingParams} params Parameters.
   */
  getOverview(
    params: DailyFoodLoggingParams
  ): Observable<GetDailyFoodLoggingResponse> {
    return this.get<GetDailyFoodLoggingResponse>(
      'user-daily-food-loggings/overview',
      params
    );
  }

  /**
   * Get process for generation nourish plus.
   *
   * @param {boolean} isWaitingProgressCompleted Whether to wait for progress to complete.
   * @returns {Observable<unknown>}
   * @memberof ReportNourishGuidelineService
   */
  getProcessGenerationNourishPlus(
    isWaitingProgressCompleted?: boolean
  ): Observable<GetProcessGenerationNourishPlusResponse> {
    return this.reportService.get(
      isWaitingProgressCompleted
        ? 'usp/process/generation-nourish-plus/progress?isWaitingProgressCompleted=true'
        : 'usp/process/generation-nourish-plus/progress'
    );
  }

  /**
   * Nutrition Guideline Ideal Calorie Intake.
   *
   * @memberof ReportNourishGuidelineService
   * @returns {Observable<DnaNutritionGuidelineCalculation>}
   */
  idealCalorieIntake(): Observable<DnaNutritionGuidelineCalculation> {
    const query = gql`
      query NutritionGuidelineCalculation {
        DNA {
          NutritionGuideline {
            Calculation {
              findByUser {
                idealCaloricIntakeUnit
                idealCaloricIntakeValue
                macronutrientPercentageFat
                macronutrientPercentageProtein
                macronutrientPercentageCarbohydrates
                macronutrientGramProtein
                macronutrientGramFat
                macronutrientGramCarbohydrates
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query
      })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'DNA.NutritionGuideline.Calculation.findByUser'
            ) as DnaNutritionGuidelineCalculation
        )
      );
  }

  /**
   * Nutrition Guideline Meal Plan.
   *
   * @memberof ReportNourishGuidelineService
   * @returns {Observable<DnaNutritionGuidelineMealPlanQueryResult>}
   */
  mealPlan(): Observable<DnaNutritionGuidelineMealPlanQueryResult> {
    const query = gql`
      query NutritionGuidelineMealPlan(
        $include: [DnaNutritionGuidelineMealPlanInclude]
      ) {
        DNA {
          NutritionGuideline {
            MealPlan {
              findByUser(include: $include) {
                results {
                  id
                  mealType
                  nutritionGuidelineId
                  cycleId
                  totalCalories
                  timeRecommendation
                  nGuidelineMealPlanItem {
                    id
                    nutritionType
                    nutritionGuidelineMealPlanId
                    title
                    description
                    total
                    unit
                    createdAt
                    updatedAt
                  }
                  nGuidelineMealPlanRecommendation {
                    id
                    nutritionGuidelineMealPlanId
                    menuRecommendationTitle
                    menuRecommendationNote
                    position
                    totalCalories
                    createdAt
                    updatedAt
                  }
                  createdAt
                  updatedAt
                }
                count
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables: {
          include: [
            'RPX_NUTRITION_GUIDELINE_MEAL_PLAN_ITEM',
            'RPX_NUTRITION_GUIDELINE_MEAL_PLAN_RECOMMENDATION'
          ]
        }
      })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'DNA.NutritionGuideline.MealPlan.findByUser'
            ) as unknown as DnaNutritionGuidelineMealPlanQueryResult
        )
      );
  }

  /**
   * Nutrition Guideline Personal Information.
   *
   * @memberof ReportNourishGuidelineService
   * @returns {Observable<DnaNutritionGuideline>}
   */
  personalInformation(): Observable<DnaNutritionGuideline> {
    const query = gql`
      query NutritionGuidelinePersonalInformation(
        $include: [DnaNutritionGuidelineInclude]
      ) {
        DNA {
          NutritionGuideline {
            findByUser(include: $include) {
              id
              goals
              weight
              nGuidelineCalculation {
                bodyMassIndex
                bodyMassIndexType
                idealCaloricIntakeUnit
                idealCaloricIntakeValue
                macronutrientPercentageCarbohydrates
                macronutrientPercentageFat
                macronutrientPercentageProtein
              }
              height
              allergy
              currentMedicalConditions
              personalMedicalHistory
              physicalActivityLevel
              physicalActivityLevelRecommendation
              exercisePerWeek
              weightGoal
              questionnaireSubmittedAt
              isNourishPlus
              nGuidelineGeneticOverview {
                id
                title
                position
                description
              }
              currentHealthGoals
              targetBestBodyDurationMonths
              previousWeight
              weightProgress
              previousGeneratedAt
              updatedAt
              createdAt
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables: {
          include: [
            'RPX_NUTRITION_GUIDELINE_SUMMARY',
            'RPX_NUTRITION_GUIDELINE_GENETIC_OVERVIEW'
          ]
        }
      })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'DNA.NutritionGuideline.findByUser'
            ) as DnaNutritionGuideline
        )
      );
  }
}
